/**
 * LaunchDarkly configuration for the training forker SIP video quality gate.
 *
 * Flag key: {@link TRAINING_FORKER_QUALITY_GATE_LD_FLAG_KEY} (`trainingForkerQualityGate`).
 * Partial JSON from LD is deep-merged with {@link DEFAULT_TRAINING_FORKER_QUALITY_GATE_CONFIG};
 * invalid values fall back to defaults per field.
 *
 * @see docs/training-forker-quality-monitor-design.md
 */
export const TRAINING_FORKER_QUALITY_GATE_LD_FLAG_KEY =
  'trainingForkerQualityGate';

/** Requires N consecutive bad polls before a signal triggers a pause. */
interface PollCountThreshold {
  minConsecutivePolls: number;
}

/** inbound-rtp freeze growth (decoder-side). */
export interface TrainingForkerFreezeQualityGate extends PollCountThreshold {
  /** Minimum accumulated freeze duration (ms) within the current streak. */
  streakTotalMs: number;
}

/** Packet loss threshold; `percent` is compared to rate × 100 (0–100 scale). */
export interface TrainingForkerPacketLossQualityGate extends PollCountThreshold {
  percent: number;
}

/** Jitter threshold in milliseconds (stats are converted from seconds). */
export interface TrainingForkerJitterQualityGate extends PollCountThreshold {
  ms: number;
}

/** outbound-rtp `qualityLimitationReason` other than `none`. */
export interface TrainingForkerEncoderLimitationQualityGate extends PollCountThreshold {
  enabled: boolean;
}

/**
 * Resolved quality gate config after merging LD flag values with defaults.
 * `shared` thresholds apply to both received (inbound-rtp) and sent feedback (remote-inbound-rtp).
 */
export interface TrainingForkerQualityGateConfig {
  /** Master switch; when false the monitor clears peer state and may reset `qualityDegraded`. */
  enabled: boolean;
  /** How often `useTrainingForkerQualityMonitor` polls WebRTC metrics. */
  pollIntervalMs: number;
  /** Interval for `training_forker_quality_health` Savvy heartbeats. */
  healthLogIntervalMs: number;
  /**
   * Delay after monitoring starts before quality can latch degraded.
   * Override via LaunchDarkly `trainingForkerQualityGate.monitoringGracePeriodMs`.
   * Set to `0` to disable the grace period.
   */
  monitoringGracePeriodMs: number;
  received: {
    freeze: TrainingForkerFreezeQualityGate;
  };
  shared: {
    packetLoss: TrainingForkerPacketLossQualityGate;
    jitter: TrainingForkerJitterQualityGate;
  };
  sent: {
    encoderLimitation: TrainingForkerEncoderLimitationQualityGate;
  };
}

type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};

export type TrainingForkerQualityGateFlagValue =
  | DeepPartial<TrainingForkerQualityGateConfig>
  | undefined
  | null;

/** Defaults used when the LD flag is absent or a field is invalid. */
export const DEFAULT_TRAINING_FORKER_QUALITY_GATE_CONFIG: TrainingForkerQualityGateConfig =
  {
    enabled: true,
    pollIntervalMs: 3000,
    healthLogIntervalMs: 60_000,
    monitoringGracePeriodMs: 10_000,
    received: {
      freeze: {
        streakTotalMs: 400,
        minConsecutivePolls: 2,
      },
    },
    shared: {
      packetLoss: {
        percent: 3,
        minConsecutivePolls: 2,
      },
      jitter: {
        ms: 30,
        minConsecutivePolls: 2,
      },
    },
    sent: {
      encoderLimitation: {
        enabled: true,
        minConsecutivePolls: 2,
      },
    },
  };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function resolveBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function resolvePositiveNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : fallback;
}

function resolvePositiveInteger(value: unknown, fallback: number): number {
  return Number.isInteger(value) && (value as number) > 0
    ? (value as number)
    : fallback;
}

function resolveNonNegativeInteger(value: unknown, fallback: number): number {
  return Number.isInteger(value) && (value as number) >= 0
    ? (value as number)
    : fallback;
}

function resolveFreezeConfig(
  value: DeepPartial<TrainingForkerFreezeQualityGate> | undefined,
  fallback: TrainingForkerFreezeQualityGate
): TrainingForkerFreezeQualityGate {
  return {
    streakTotalMs: resolvePositiveNumber(
      value?.streakTotalMs,
      fallback.streakTotalMs
    ),
    minConsecutivePolls: resolvePositiveInteger(
      value?.minConsecutivePolls,
      fallback.minConsecutivePolls
    ),
  };
}

function resolvePacketLossConfig(
  value: DeepPartial<TrainingForkerPacketLossQualityGate> | undefined,
  fallback: TrainingForkerPacketLossQualityGate
): TrainingForkerPacketLossQualityGate {
  return {
    percent: resolvePositiveNumber(value?.percent, fallback.percent),
    minConsecutivePolls: resolvePositiveInteger(
      value?.minConsecutivePolls,
      fallback.minConsecutivePolls
    ),
  };
}

function resolveJitterConfig(
  value: DeepPartial<TrainingForkerJitterQualityGate> | undefined,
  fallback: TrainingForkerJitterQualityGate
): TrainingForkerJitterQualityGate {
  return {
    ms: resolvePositiveNumber(value?.ms, fallback.ms),
    minConsecutivePolls: resolvePositiveInteger(
      value?.minConsecutivePolls,
      fallback.minConsecutivePolls
    ),
  };
}

function resolveEncoderLimitationConfig(
  value: DeepPartial<TrainingForkerEncoderLimitationQualityGate> | undefined,
  fallback: TrainingForkerEncoderLimitationQualityGate
): TrainingForkerEncoderLimitationQualityGate {
  return {
    enabled: resolveBoolean(value?.enabled, fallback.enabled),
    minConsecutivePolls: resolvePositiveInteger(
      value?.minConsecutivePolls,
      fallback.minConsecutivePolls
    ),
  };
}

/** Reads the raw JSON value from LaunchDarkly flags. */
export const getTrainingForkerQualityGateFlag = (
  flags: Record<string, unknown>
): TrainingForkerQualityGateFlagValue => {
  const rawValue = flags[TRAINING_FORKER_QUALITY_GATE_LD_FLAG_KEY];
  return isRecord(rawValue) ? rawValue : undefined;
};

/** Merges a partial LD flag payload with {@link DEFAULT_TRAINING_FORKER_QUALITY_GATE_CONFIG}. */
export const resolveTrainingForkerQualityGateConfig = (
  flagValue: TrainingForkerQualityGateFlagValue
): TrainingForkerQualityGateConfig => {
  const root = isRecord(flagValue) ? flagValue : {};
  const received = isRecord(root.received) ? root.received : {};
  const shared = isRecord(root.shared) ? root.shared : {};
  const sent = isRecord(root.sent) ? root.sent : {};

  return {
    enabled: resolveBoolean(
      root.enabled,
      DEFAULT_TRAINING_FORKER_QUALITY_GATE_CONFIG.enabled
    ),
    pollIntervalMs: resolvePositiveInteger(
      root.pollIntervalMs,
      DEFAULT_TRAINING_FORKER_QUALITY_GATE_CONFIG.pollIntervalMs
    ),
    healthLogIntervalMs: resolvePositiveInteger(
      root.healthLogIntervalMs,
      DEFAULT_TRAINING_FORKER_QUALITY_GATE_CONFIG.healthLogIntervalMs
    ),
    monitoringGracePeriodMs: resolveNonNegativeInteger(
      root.monitoringGracePeriodMs,
      DEFAULT_TRAINING_FORKER_QUALITY_GATE_CONFIG.monitoringGracePeriodMs
    ),
    received: {
      freeze: resolveFreezeConfig(
        isRecord(received.freeze) ? received.freeze : undefined,
        DEFAULT_TRAINING_FORKER_QUALITY_GATE_CONFIG.received.freeze
      ),
    },
    shared: {
      packetLoss: resolvePacketLossConfig(
        isRecord(shared.packetLoss) ? shared.packetLoss : undefined,
        DEFAULT_TRAINING_FORKER_QUALITY_GATE_CONFIG.shared.packetLoss
      ),
      jitter: resolveJitterConfig(
        isRecord(shared.jitter) ? shared.jitter : undefined,
        DEFAULT_TRAINING_FORKER_QUALITY_GATE_CONFIG.shared.jitter
      ),
    },
    sent: {
      encoderLimitation: resolveEncoderLimitationConfig(
        isRecord(sent.encoderLimitation) ? sent.encoderLimitation : undefined,
        DEFAULT_TRAINING_FORKER_QUALITY_GATE_CONFIG.sent.encoderLimitation
      ),
    },
  };
};
